import { Skeleton } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { type JSX, useMemo, useState } from 'react';

import { chartColor, chartFont } from 'Components/BaseCharts/charts.common';
import { Button } from 'Components/Buttons/Buttons';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { Text } from 'Components/Text/Text';
import { ALL_RISKS, RiskCategoryResponse, RiskResponse } from 'Models/RiskRegister';

import { RiskModal } from './RiskModal';
import styles from './RiskRegisterDashboard.module.css';

enum Modals {
    NONE,
    DISPLAY_RISKS_WITH_RESIDUAL_RATING,
    DISPLAY_RISKS_WITHOUT_RESIDUAL_RATING,
}

/** Reference: https://echarts.apache.org/en/api.html#events.Mouse%20events */
interface eChartsEventsMouseClick {
    data: {
        value: number[];
    };
}

/** Reference: https://echarts.apache.org/en/option.html#series-heatmap.data */
interface eChartsOptionSeriesHeatmapDataValue {
    value: [number, number, number];
    itemStyle: {
        color: string;
    };
}

/** Reference: https://echarts.apache.org/en/option.html#tooltip.formatter */
interface eChartsOptionTooltipFormatterParams {
    value: number[];
}

export interface RiskMatrixProps {
    error?: Error;
    riskCategories?: RiskCategoryResponse[];
    risks?: RiskResponse[];
}

/** The color of each cell in the matrix, indexed as [impact][probability]. Note that this matrix is shown in the SummIT User Manual, so if we change colors/labels/ordering here, we should update the manual too. */
const CELL_COLORS = [
    ['#31673D', '#31673D', '#8DDA41', '#8DDA41', '#D0DA41'],
    ['#31673D', '#8DDA41', '#8DDA41', '#D0DA41', '#FFC107'],
    ['#8DDA41', '#8DDA41', '#D0DA41', '#FFC107', '#FFC107'],
    ['#8DDA41', '#D0DA41', '#FFC107', '#FFC107', '#DC3545'],
    ['#D0DA41', '#FFC107', '#FFC107', '#DC3545', '#DC3545'],
];

/**
 * Displays a 5x5 heatmap of Risks by current residual impact and probability, filterable by Risk Category. Used on the Risk Management tab of the Dashboards page.
 *
 * Note: `echarts-for-react` deep-compares `option` on every render and calls `setOption()` when it differs, which makes the heatmap rebuild every cell (a visible flash).
 * Functions (such as the tooltip formatter) are compared by reference, so `options` and `onEvents` are memoized to keep them stable while the parent page re-renders as its other data loads.
 */
export const RiskMatrix = (props: RiskMatrixProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE); // Displays a modal with a list of Risks.
    const [selectedRiskCategory, setSelectedRiskCategory] = useState<string>(ALL_RISKS); // Filters Risks by category. Updated when the drop-down selector changes.
    const [selectedRisksForModal, setSelectedRisksForModal] = useState<RiskResponse[]>([]); // Risks to show in a modal. Updated when a matrix cell is clicked.

    /**
     * Organizes the Risks in the selected category by impact and probability.
     * `riskMatrix` is a 3D array (5x5xN: [impact][probability][risks]); `risksWithoutResidualRating` holds Risks that do not have a current residual risk rating.
     */
    const { riskMatrix, risksWithoutResidualRating } = useMemo(() => {
        const riskMatrix: RiskResponse[][][] = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
        const risksWithoutResidualRating: RiskResponse[] = [];

        props.risks?.forEach((risk) => {
            if (selectedRiskCategory === ALL_RISKS || selectedRiskCategory === risk.category.id) {
                if (risk.current_risk_impact === undefined || risk.current_risk_probability === undefined) {
                    risksWithoutResidualRating.push(risk);
                } else {
                    const impact = Math.ceil(risk.current_risk_impact) - 1;
                    const probability = Math.ceil(risk.current_risk_probability) - 1;
                    riskMatrix[impact][probability].push(risk);
                }
            }
        });

        return { riskMatrix, risksWithoutResidualRating };
    }, [props.risks, selectedRiskCategory]);

    /** Reference: https://echarts.apache.org/en/option.html#series-heatmap */
    const options = useMemo(() => {
        const textFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);
        const textColor = getComputedStyle(document.documentElement).getPropertyValue(chartColor);

        const axisLabelStyle = {
            color: textColor,
            fontFamily: textFont,
        };
        const axisLabels = [
            { value: 'Very Low'.toUpperCase(), textStyle: axisLabelStyle },
            { value: 'Low'.toUpperCase(), textStyle: axisLabelStyle },
            { value: 'Medium'.toUpperCase(), textStyle: axisLabelStyle },
            { value: 'High'.toUpperCase(), textStyle: axisLabelStyle },
            { value: 'Very High'.toUpperCase(), textStyle: axisLabelStyle },
        ];

        // Ordered by probability, then impact, which is the order in which ECharts describes the data in its ARIA label.
        const chartData: eChartsOptionSeriesHeatmapDataValue[] = [];
        for (let probability = 0; probability <= 4; probability++) {
            for (let impact = 0; impact <= 4; impact++) {
                chartData.push({ value: [impact, probability, riskMatrix[impact][probability].length], itemStyle: { color: CELL_COLORS[impact][probability] } });
            }
        }

        return {
            aria: {
                show: true,
            },
            tooltip: {
                position: 'top',
                formatter: (params: eChartsOptionTooltipFormatterParams) => {
                    const [x, y, val] = params.value;
                    return `Impact: ${axisLabels[x].value}<br />Probability: ${axisLabels[y].value}<br />Risks: ${val}`;
                },
            },
            grid: {
                containLabel: true,
                top: 20,
                bottom: 20,
            },
            xAxis: {
                axisLine: {
                    lineStyle: {
                        color: '#000000',
                    },
                },
                data: axisLabels,
                name: 'Impact'.toUpperCase(),
                nameGap: 30,
                nameLocation: 'middle',
                nameTextStyle: {
                    color: textColor,
                    fontFamily: textFont,
                },
            },
            yAxis: {
                axisLine: {
                    lineStyle: {
                        color: '#000000',
                    },
                },
                data: axisLabels,
                name: 'Probability'.toUpperCase(),
                nameGap: 75,
                nameLocation: 'middle',
                nameTextStyle: {
                    color: textColor,
                    fontFamily: textFont,
                },
            },
            visualMap: {
                show: false,
            },
            series: [
                {
                    data: chartData,
                    itemStyle: {
                        borderColor: '#000000',
                        borderWidth: 1,
                    },
                    label: {
                        color: '#000000',
                        fontFamily: textFont,
                        show: true,
                    },
                    name: 'Risk Matrix',
                    type: 'heatmap',
                },
            ],
        };
    }, [riskMatrix]);

    /** Pop a modal that shows the corresponding risks for whatever cell was clicked. */
    const onEvents = useMemo(
        () => ({
            click: (params: eChartsEventsMouseClick) => {
                setSelectedRisksForModal(riskMatrix[params.data.value[0]][params.data.value[1]]);
                setModalState(Modals.DISPLAY_RISKS_WITH_RESIDUAL_RATING);
            },
        }),
        [riskMatrix]
    );

    if (props.error) {
        return (
            <Text variant="Header4" color="lightGray">
                {props.error.message}
            </Text>
        );
    } else if (props.riskCategories === undefined || props.risks === undefined) {
        return <Skeleton variant="rounded" height={600} width={'100%'} />;
    } else {
        return (
            <>
                {modalState === Modals.DISPLAY_RISKS_WITH_RESIDUAL_RATING && <RiskModal hideModal={() => setModalState(Modals.NONE)} risks={selectedRisksForModal} />}
                {modalState === Modals.DISPLAY_RISKS_WITHOUT_RESIDUAL_RATING && <RiskModal hideModal={() => setModalState(Modals.NONE)} risks={risksWithoutResidualRating} showImpactAndProbability={false} />}
                <div className={styles.dashletHeaderWithTitleWithFilter}>
                    <Text variant="Header2" color="white">
                        Risk Matrix
                    </Text>
                    <FormFieldSelectDark formFieldId="selectedRiskCategory" formFieldLabel="Risk Category" handleChange={(value: ChangeEventType) => setSelectedRiskCategory(value as string)} options={props.riskCategories.map((riskCategory) => ({ label: riskCategory.title, value: riskCategory.id }))} selectedOption={selectedRiskCategory} />
                </div>
                <ReactECharts onEvents={onEvents} option={options} style={{ height: 600, width: '100%' }} />
                <Button variant="linkText" size="sm" onClick={() => setModalState(Modals.DISPLAY_RISKS_WITHOUT_RESIDUAL_RATING)}>
                    {`${risksWithoutResidualRating.length} risk${risksWithoutResidualRating.length === 1 ? '' : 's'} do${risksWithoutResidualRating.length === 1 ? 'es' : ''} not have a current residual risk rating`}
                </Button>
            </>
        );
    }
};
